import type { EarlyPaymentRequestRow, EarlyPaymentRequestStatus } from "./earlyPaymentRequestsApi";

export type EarlyPaymentStatusFilter = "all" | EarlyPaymentRequestStatus;

export type EarlyPaymentSortKey =
  | "requested_at"
  | "requested_by"
  | "vendor_invoice_number"
  | "supplier_name"
  | "total_amount"
  | "gipi_number"
  | "status";

export const EARLY_PAYMENT_STATUS_LABELS: Record<EarlyPaymentRequestStatus, string> = {
  requested: "Requested",
  paid: "Paid",
  cancelled: "Cancelled",
};

export function earlyPaymentRequesterLabel(row: Pick<
  EarlyPaymentRequestRow,
  "requested_by_display_name" | "requested_by_email"
>): string {
  const name = row.requested_by_display_name?.trim() ?? "";
  if (name !== "") {
    return name;
  }
  const email = row.requested_by_email?.trim() ?? "";
  if (email !== "") {
    return email;
  }
  return "the requester";
}

export function earlyPaymentFiltersActive(status: EarlyPaymentStatusFilter, search: string): boolean {
  return status !== "all" || search.trim() !== "";
}

export function filterEarlyPaymentRequests(
  rows: EarlyPaymentRequestRow[],
  status: EarlyPaymentStatusFilter,
  search: string,
): EarlyPaymentRequestRow[] {
  const needle = search.trim().toLowerCase();
  return rows.filter((row) => {
    if (status !== "all" && row.status !== status) {
      return false;
    }
    if (needle === "") {
      return true;
    }
    const haystack = [
      row.vendor_invoice_number,
      row.supplier_name,
      row.gipi_number,
      earlyPaymentRequesterLabel(row),
      EARLY_PAYMENT_STATUS_LABELS[row.status],
    ]
      .filter((part): part is string => part != null && part.trim() !== "")
      .join(" ")
      .toLowerCase();
    return haystack.includes(needle);
  });
}

function compareText(left: string, right: string): number {
  return left.localeCompare(right, undefined, { sensitivity: "base", numeric: true });
}

function sortValue(row: EarlyPaymentRequestRow, key: EarlyPaymentSortKey): string | number {
  switch (key) {
    case "requested_at":
      return Date.parse(row.requested_at) || 0;
    case "requested_by":
      return earlyPaymentRequesterLabel(row);
    case "vendor_invoice_number":
      return row.vendor_invoice_number;
    case "supplier_name":
      return row.supplier_name?.trim() ?? "";
    case "total_amount": {
      const amount = Number.parseFloat(row.total_amount ?? "");
      return Number.isFinite(amount) ? amount : Number.NEGATIVE_INFINITY;
    }
    case "gipi_number":
      return row.gipi_number?.trim() ?? "";
    case "status":
      return EARLY_PAYMENT_STATUS_LABELS[row.status];
    default: {
      const exhaustive: never = key;
      return exhaustive;
    }
  }
}

export function sortEarlyPaymentRequests(
  rows: EarlyPaymentRequestRow[],
  key: EarlyPaymentSortKey,
  direction: "asc" | "desc",
): EarlyPaymentRequestRow[] {
  const copy = [...rows];
  copy.sort((left, right) => {
    const leftValue = sortValue(left, key);
    const rightValue = sortValue(right, key);
    const compared = typeof leftValue === "number" && typeof rightValue === "number"
      ? leftValue - rightValue
      : compareText(String(leftValue), String(rightValue));
    if (compared !== 0) {
      return direction === "asc" ? compared : -compared;
    }
    return compareText(right.requested_at, left.requested_at);
  });
  return copy;
}
